import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminHeader from '../../components/ui/AdminHeader';
import AdminClientSelector from '../../components/ui/AdminClientSelector';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CameraCapture from './components/CameraCapture';
import ImageGallery from './components/ImageGallery';
import ItemDetailsForm from './components/ItemDetailsForm';
import ConfirmationModal from './components/ConfirmationModal';
import QRCodeLabel from '../../components/ui/QRCodeLabel';
import { createInventoryItem, uploadInventoryPhoto, addInventoryPhotoRecord } from '../../services/laseaInventoryService';

const AddInventoryItem = () => {
  const navigate = useNavigate();
  const { user, userRole, userProfile } = useAuth();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [images, setImages] = useState([]);
  const [primaryImageIndex, setPrimaryImageIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [formData, setFormData] = useState({
    liin: '',
    name: '',
    condition: '',
    arrivalDate: '',
    location: 'Portland, OR',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showQRLabel, setShowQRLabel] = useState(false);
  const [savedItem, setSavedItem] = useState(null);

  useEffect(() => {
    if (userRole === 'admin' || userRole === 'mover') {
      const storedClient = localStorage.getItem('selectedClient');
      if (storedClient && storedClient !== 'undefined') {
        try {
          setSelectedClient(JSON.parse(storedClient));
        } catch (e) {
          console.error('Failed to parse selectedClient from localStorage', e);
          localStorage.removeItem('selectedClient');
        }
      }
    } else if (user) {
      // If regular client, they force-add items to their own database record
      setSelectedClient({
        id: user.id || userProfile?.id,
        name: userProfile?.fullName || user.email
      });
    }
  }, [user, userRole, userProfile]);

  useEffect(() => {
    const hasData = images?.length > 0 ||
      formData?.name ||
      formData?.condition ||
      formData?.arrivalDate ||
      formData?.notes;
    setHasUnsavedChanges(hasData);
  }, [images, formData]);

  const validateForm = () => {
    const newErrors = {};

    if (images?.length === 0) {
      newErrors.images = 'At least one image is required';
    }

    if (!formData?.name?.trim()) {
      newErrors.name = 'Furniture name is required';
    }

    if (!formData?.condition) {
      newErrors.condition = 'Condition status is required';
    }

    if (!formData?.arrivalDate) {
      newErrors.arrivalDate = 'Arrival date is required';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      alert('Please fix the missing required fields before saving.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (isSaving) return;

    if (!selectedClient?.id) {
      alert('You must be logged in to add inventory items');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);

      // 1. Create inventory item in Supabase
      const inventoryData = {
        clientId: selectedClient?.id,
        itemName: formData?.name,
        description: formData?.notes || null,
        category: 'furniture',
        condition: formData?.condition,
        purchasePrice: null,
        currentValue: null,
        location: formData?.location || 'Portland, OR',
        notes: formData?.notes,
        isActive: true
      };

      const timeout = (ms, msg) => new Promise((_, reject) => setTimeout(() => reject(new Error(msg)), ms));

      console.log('Creating inventory item with data:', inventoryData);
      const createdItem = await Promise.race([
        createInventoryItem(inventoryData),
        timeout(15000, 'Database item creation timed out. Please check your connection.')
      ]);
      console.log('Created item:', createdItem);

      // 2. Upload photos to Supabase Storage
      if (images?.length > 0) {
        console.log(`Uploading ${images?.length} photos...`);
        for (let i = 0; i < images?.length; i++) {
          const image = images?.[i];

          // Convert blob to File object with proper MIME type
          let file;
          try {
            if (image?.blob instanceof File) {
              file = image.blob;
              console.log(`Image ${i + 1} is File: ${file.name} (${file.size} bytes)`);
            } else if (image?.blob instanceof Blob) {
              console.log(`Image ${i + 1} is Blob (${image.blob.size} bytes), converting to File...`);
              file = new File([image.blob], `photo-${Date.now()}-${i}.jpg`, { type: 'image/jpeg' });
            } else {
              console.error(`Image ${i + 1} blob is invalid:`, image?.blob);
              console.warn('Skipping invalid image blob');
              continue;
            }
          } catch (err) {
            console.error('Error preparing file:', err);
            continue;
          }

          try {
            // Upload photo to storage
            const progressMsg = `Uploading photo ${i + 1} of ${images.length}...`;
            console.log('📤', progressMsg);
            setUploadProgress(progressMsg);

            const uploadReq = uploadInventoryPhoto(file, createdItem?.id);
            const photoPath = await Promise.race([
              uploadReq,
              timeout(30000, `Photo ${i + 1} upload timed out. Please check your connection.`)
            ]);
            console.log('Photo uploaded to:', photoPath);

            // Add photo record to database
            await addInventoryPhotoRecord(
              createdItem?.id,
              photoPath,
              i === 0, // First image is primary
              i // Display order
            );
          } catch (uploadError) {
            console.error(`Failed to upload photo ${i + 1}:`, uploadError);
            // Continue loop to save other images if possible
          }
        }
      }

      setIsSubmitting(true);
      setShowConfirmation(true);
      setSavedItem({ liin: formData?.liin, name: formData?.name });
      setHasUnsavedChanges(false);

      // Removed automatic navigation - let user interact with modal
    } catch (error) {
      console.error('Error saving item:', error);

      // Provide detailed error message
      let errorMessage = 'Failed to save item';

      // Check for network errors specifically
      if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network connection failed')) {
        errorMessage = '❌ Network Error: Unable to connect to the database.\n\n';
        errorMessage += 'This could be due to:\n';
        errorMessage += '• Internet connection issues\n';
        errorMessage += '• Supabase service configuration\n';
        errorMessage += '• CORS or firewall blocking the request\n\n';
        errorMessage += 'Please check your connection, refresh your browser, and try again.';
      } else if (error?.code === '23503' || error?.message?.includes('foreign key constraint')) {
        errorMessage = '❌ Invalid Client Selected\n\n';
        errorMessage += 'The system could not link this item because the selected client no longer exists in the database (they may have been deleted). ';
        errorMessage += 'Please slide open the menu and re-select a valid client before saving.';

        // Clear the corrupted localStorage value
        localStorage.removeItem('selectedClient');
      } else if (error?.message) {
        errorMessage += `: ${error?.message}`;
        if (error?.details) {
          errorMessage += `\n\nDetails: ${error?.details}`;
        }
        if (error?.hint) {
          errorMessage += `\n\nHint: ${error?.hint}`;
        }
      }

      alert(errorMessage);
    } finally {
      setIsSaving(false);
      setIsSubmitting(false);
      setUploadProgress(null);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelModal(true);
    } else {
      navigate('/client-inventory-management');
    }
  };

  const confirmCancel = () => {
    setShowCancelModal(false);
    navigate('/client-inventory-management');
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    navigate('/client-inventory-management');
  };

  const handlePrintQRFromConfirmation = () => {
    setShowConfirmation(false);
    setShowQRLabel(true);
  };

  const handleQRLabelClose = () => {
    setShowQRLabel(false);
    navigate('/client-inventory-management');
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />
      <div className="flex pt-16">
        <AdminClientSelector
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onClientSelect={handleClientSelect}
        />

        <main className="flex-1 p-4 lg:p-6 overflow-x-hidden">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="lg:hidden p-2 rounded-md hover:bg-muted transition-smooth touch-target"
                  aria-label="Open client selector"
                >
                  <Icon name="Menu" size={24} />
                </button>

                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
                    Add Inventory Item
                  </h1>
                  {selectedClient && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Adding item for {selectedClient?.name}
                    </p>
                  )}
                </div>
              </div>

              <Button
                variant="ghost"
                onClick={handleCancel}
                iconName="X"
                iconSize={20}
              >
                <span className="sr-only">Close</span>
              </Button>
            </div>

            {!selectedClient && (
              <div className="mb-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-warning mb-1">No Client Selected</p>
                    <p className="text-xs text-muted-foreground">
                      Please select a client from the sidebar before adding inventory items.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {errors?.images && (
              <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="XCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-error mb-1">Image Required</p>
                    <p className="text-xs text-muted-foreground">
                      {errors?.images}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-6">
              <CameraCapture
                images={images}
                onImagesChange={setImages}
                primaryImageIndex={primaryImageIndex}
                onPrimaryImageChange={setPrimaryImageIndex}
              />

              <ImageGallery
                images={images}
                onImagesChange={setImages}
                primaryImageIndex={primaryImageIndex}
                onPrimaryImageChange={setPrimaryImageIndex}
              />

              <ItemDetailsForm
                formData={formData}
                onFormDataChange={setFormData}
                errors={errors}
              />

              <div className="flex flex-col sm:flex-row items-center gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  fullWidth
                  iconName="X"
                  iconPosition="left"
                  disabled={isSaving}
                >
                  Cancel
                </Button>

                <Button
                  variant="default"
                  onClick={handleSubmit}
                  fullWidth
                  iconName="Save"
                  iconPosition="left"
                  loading={isSaving}
                  disabled={!selectedClient}
                >
                  {isSaving ? (uploadProgress || 'Saving Item...') : 'Save Item'}
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={confirmCancel}
        onPrintQR={() => { }}
        title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to leave? All progress will be lost."
        type="warning"
      />
      <ConfirmationModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onConfirm={() => navigate('/client-inventory-management')}
        onPrintQR={() => { }}
        title="Item Added Successfully!"
        message="The inventory item has been saved and is now visible to the client."
        type="success"
      />
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={handleConfirmationClose}
        onConfirm={handleConfirmationClose}
        onPrintQR={handlePrintQRFromConfirmation}
        title="Item Added Successfully!"
        message="The inventory item has been added to your collection."
        type="success"
      />

      {showQRLabel && savedItem && (
        <QRCodeLabel
          serialNumber={savedItem?.id}
          itemName={savedItem?.name}
          onClose={handleQRLabelClose}
        />
      )}
    </div>
  );
};

export default AddInventoryItem;